import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus, Search, Edit, Trash2, Loader2, MoreHorizontal,
  Printer, Eye, FileText, RotateCcw, DollarSign
} from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import LineItemsForm from '../../components/LineItemsForm';

import { generateDocumentHTML } from '../../lib/documentTemplates';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  issued: 'bg-blue-100 text-blue-700',
  applied: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const REASON_OPTIONS = [
  { value: 'return', label: 'Product Return' },
  { value: 'damaged', label: 'Damaged Goods' },
  { value: 'overcharge', label: 'Overcharge Correction' },
  { value: 'discount', label: 'Post-Sale Discount' },
  { value: 'cancellation', label: 'Order Cancellation' },
  { value: 'other', label: 'Other' },
];

const CreditNotes = () => {
  const { currentCompany } = useAuth();
  const [creditNotes, setCreditNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    invoice_id: '',
    items: [{ id: '1', product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: '0', tax_amount: 0, subtotal: 0, line_total: 0 }],
    discount: 0,
    discount_type: 'fixed',
    reason: 'return',
    notes: '',
    status: 'draft',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [notesRes, clientsRes, invoicesRes] = await Promise.all([
        api.get('/credit-notes'),
        api.get('/clients'),
        api.get('/invoices')
      ]);
      setCreditNotes(notesRes.data);
      setClients(clientsRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getClient = (id) => clients.find(c => c.id === id);

  const calculateTotals = (items, discount, discountType) => {
    const subtotal = items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const tax = items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
    const discountAmount = discountType === 'percent' ? (subtotal * (parseFloat(discount) || 0) / 100) : (parseFloat(discount) || 0);
    const total = subtotal + tax - discountAmount;
    return { subtotal, tax, discount: discountAmount, total };
  };

  const openForm = (note = null) => {
    if (note) {
      setEditingNote(note);
      setFormData({
        client_id: note.client_id || '',
        invoice_id: note.invoice_id || '',
        items: note.items?.length > 0 ? note.items : [{ id: '1', product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: '0', tax_amount: 0, subtotal: 0, line_total: 0 }],
        discount: note.discount || 0,
        discount_type: note.discount_type || 'fixed',
        reason: note.reason || 'return',
        notes: note.notes || '',
        status: note.status || 'draft',
      });
    } else {
      setEditingNote(null);
      setFormData({
        client_id: '', invoice_id: '',
        items: [{ id: '1', product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: '0', tax_amount: 0, subtotal: 0, line_total: 0 }],
        discount: 0, discount_type: 'fixed', reason: 'return', notes: '', status: 'draft',
      });
    }
    setIsFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    setSubmitting(true);
    try {
      const totals = calculateTotals(formData.items, formData.discount, formData.discount_type);
      const payload = { ...formData, ...totals, discount_amount: totals.discount };

      if (editingNote) {
        await api.put(`/credit-notes/${editingNote.id}`, payload);
        toast.success('Credit Note updated');
      } else {
        await api.post('/credit-notes', payload);
        toast.success('Credit Note created');
      }
      setIsFormOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this credit note?')) {
      try {
        await api.delete(`/credit-notes/${id}`);
        toast.success('Deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed');
      }
    }
  };

  const openPreview = (note) => {
    setSelectedNote(note);
    const client = getClient(note.client_id);
    const noteDoc = {
      ...note,
      client_name: client?.name || 'Client',
      client_email: client?.email || '',
    };
    let html = generateDocumentHTML('classic', 'credit_note', noteDoc, currentCompany);
    html = html.replace('INVOICE', 'CREDIT NOTE');
    setPreviewHtml(html);
    setIsPreviewOpen(true);
  };

  const handlePrint = (note) => {
    const client = getClient(note.client_id);
    const noteDoc = { ...note, client_name: client?.name || '' };
    let html = generateDocumentHTML('classic', 'credit_note', noteDoc, currentCompany);
    html = html.replace('INVOICE', 'CREDIT NOTE');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  // Auto-fill from invoice
  const handleInvoiceChange = (invoiceId) => {
    const invoice = invoices.find(i => i.id === invoiceId);
    if (invoice) {
      setFormData({
        ...formData,
        invoice_id: invoiceId,
        client_id: invoice.client_id,
        items: invoice.items || formData.items,
      });
    }
  };

  const filteredNotes = creditNotes.filter(n =>
    n.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getClient(n.client_id)?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalCredit = creditNotes.reduce((sum, n) => sum + (n.total || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Credit Notes</h1>
          <p className="text-slate-500 mt-1">Manage returns, refunds, and credit adjustments</p>
        </div>
        <Button onClick={() => openForm()} className="bg-teal-700 hover:bg-teal-800">
          <Plus className="h-4 w-4 mr-2" />Create Credit Note
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div><p className="text-sm text-slate-500">Total Credit Issued</p><p className="text-2xl font-bold text-red-600">-${totalCredit.toLocaleString()}</p></div>
            <DollarSign className="h-8 w-8 text-red-500" />
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div><p className="text-sm text-slate-500">Credit Notes</p><p className="text-2xl font-bold">{creditNotes.length}</p></div>
            <FileText className="h-8 w-8 text-blue-500" />
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div><p className="text-sm text-slate-500">Pending</p><p className="text-2xl font-bold">{creditNotes.filter(n => n.status === 'draft' || n.status === 'issued').length}</p></div>
            <RotateCcw className="h-8 w-8 text-amber-500" />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search credit notes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Credit Note #</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredNotes.length > 0 ? (
                filteredNotes.map((note) => (
                  <TableRow key={note.id}>
                    <TableCell className="font-medium">#{note.id.slice(0, 8).toUpperCase()}</TableCell>
                    <TableCell>{getClient(note.client_id)?.name || '-'}</TableCell>
                    <TableCell>{note.invoice_id ? `#${note.invoice_id.slice(0, 8)}` : '-'}</TableCell>
                    <TableCell><Badge variant="outline">{REASON_OPTIONS.find(r => r.value === note.reason)?.label || note.reason}</Badge></TableCell>
                    <TableCell className="text-right font-medium text-red-600">-${(note.total || 0).toLocaleString()}</TableCell>
                    <TableCell><Badge className={STATUS_COLORS[note.status]}>{note.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openPreview(note)}><Eye className="h-4 w-4 mr-2" />Preview</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handlePrint(note)}><Printer className="h-4 w-4 mr-2" />Print</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openForm(note)}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(note.id)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={7} className="h-32 text-center text-slate-500"><FileText className="h-8 w-8 mx-auto mb-2 text-slate-300" />No credit notes found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingNote ? 'Edit Credit Note' : 'Create Credit Note'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit}>
            {/* Reference Invoice */}
            <div className="mb-4 p-4 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium">Reference Invoice (Optional)</Label>
              <Select value={formData.invoice_id} onValueChange={handleInvoiceChange}>
                <SelectTrigger className="mt-2"><SelectValue placeholder="Select invoice" /></SelectTrigger>
                <SelectContent>
                  {invoices.map(inv => (
                    <SelectItem key={inv.id} value={inv.id}>#{inv.id.slice(0, 8)} - ${inv.total} ({getClient(inv.client_id)?.name})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <LineItemsForm
              items={formData.items}
              onItemsChange={(items) => setFormData({ ...formData, items })}
              clients={clients}
              onClientsChange={setClients}
              selectedClientId={formData.client_id}
              onClientChange={(id) => setFormData({ ...formData, client_id: id })}
              discount={formData.discount}
              onDiscountChange={(val) => setFormData({ ...formData, discount: val })}
              discountType={formData.discount_type}
              onDiscountTypeChange={(val) => setFormData({ ...formData, discount_type: val })}
            />

            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="grid gap-2">
                <Label>Reason *</Label>
                <Select value={formData.reason} onValueChange={(val) => setFormData({ ...formData, reason: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REASON_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="issued">Issued</SelectItem>
                    <SelectItem value="applied">Applied</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2 mt-4">
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} />
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editingNote ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Credit Note Preview</DialogTitle></DialogHeader>
          
          <div className="border rounded-lg overflow-hidden mt-4" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>Close</Button>
            <Button variant="outline" onClick={() => selectedNote && handlePrint(selectedNote)}><Printer className="h-4 w-4 mr-2" />Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditNotes;
